/**
 * QuizResults — post-session results screen.
 *
 * Design source: /tmp/arianna-design/ios-reader/project/quiz.jsx QuizResults
 *
 * Shows: hero score + serif narrative, StatPill row, WrongCard list, skipped list.
 * Scoring: uses computeQuizSessionScore from @arianna/reader-core when
 * `storedScore` is not available (live session), otherwise uses the stored score.
 */

import * as React from 'react'
import { useMemo, useState } from 'react'
import { clsx } from 'clsx'
import type { BundleQuizQuestion } from '@arianna/shared'
import { computeQuizSessionScore } from '@arianna/reader-core'
import { StatPill, WrongCard, CorrectCard } from './primitives.js'

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface QuizResultsProps {
  /** Questions included in this session (ordered by question_ids) */
  questions: BundleQuizQuestion[]
  /** Answers map: question ID → selected option index (null = skipped) */
  answers: Record<string, number | null>
  /** Pre-computed score (from server); if null, computed live from questions+answers */
  storedScore?: { correct: number; wrong: number; skipped: number; total: number } | null
  /** True when viewing a stored session read-only (hides "Vai a Da rivedere" CTA) */
  isReadOnly?: boolean
  /** True when launched from review mode (changes exit label) */
  isReviewMode?: boolean
  onExit: () => void
  onRetry: () => void
  onGoReview?: () => void
  /** Navigate to the chapter associated with a wrong question */
  onGoToChapter?: (subParaId: string) => void
  /** Navigate to the Padronanza section (`?vista=padronanza`). Omit to hide the CTA. */
  onGoMastery?: () => void
}

// ---------------------------------------------------------------------------
// QuizResults component
// ---------------------------------------------------------------------------

export function QuizResults({
  questions,
  answers,
  storedScore,
  isReadOnly = false,
  isReviewMode = false,
  onExit,
  onRetry,
  onGoReview,
  onGoToChapter,
  onGoMastery,
}: QuizResultsProps) {
  const questionIds = useMemo(() => questions.map((q) => q.id), [questions])

  const score = useMemo(
    () => storedScore ?? computeQuizSessionScore(questionIds, answers, questions),
    [storedScore, questionIds, answers, questions],
  )

  const { correct, wrong, skipped, total } = score

  const [filter, setFilter] = useState<'all' | 'wrong'>('all')

  // Classify each question, preserving original session order
  type ResultItem = {
    q: BundleQuizQuestion
    status: 'wrong' | 'correct' | 'skipped'
    givenIdx: number | null
  }
  const items = useMemo<ResultItem[]>(
    () =>
      questions.map((q) => {
        const ans = answers[q.id]
        if (ans === undefined || ans === null) return { q, status: 'skipped', givenIdx: null }
        if (ans !== q.correctOptionIdx) return { q, status: 'wrong', givenIdx: ans }
        return { q, status: 'correct', givenIdx: ans }
      }),
    [questions, answers],
  )
  const visibleItems = useMemo(
    () => (filter === 'wrong' ? items.filter((i) => i.status === 'wrong') : items),
    [items, filter],
  )

  const pct = total > 0 ? Math.round((correct / total) * 100) : 0
  // Score color: green ≥75%, gold ≥50%, red <50%
  const headlineColorClass = pct >= 75 ? 'text-green' : pct >= 50 ? 'text-gold' : 'text-red'

  const narrativeText =
    pct >= 80
      ? `Sei solido su questo capitolo. Il nucleo dei tre livelli ti è chiaro.`
      : pct >= 60
        ? `Hai il quadro generale, ma ${wrong} domand${wrong === 1 ? 'a' : 'e'} ti ${wrong === 1 ? 'ha' : 'hanno'} fatto inciampare.`
        : `Vale la pena rileggere il paragrafo prima di riprovare — la base non è ancora ferma.`

  return (
    <div className="flex flex-col h-full bg-bg font-sans overflow-hidden">
      {/* ── Top bar ───────────────────────────────────────────────── */}
      {/* TASK-014 dedup: the "Risultati verifica · {bundleTitle}" breadcrumb
          duplicated the course shell's own header/tabs — removed; the back
          button (functionally useful — exits the session) stays. */}
      <div className="flex items-center px-8 py-[14px] border-b border-line shrink-0">
        <button
          onClick={onExit}
          className="bg-transparent border-none text-ink-3 text-sm cursor-pointer font-sans flex items-center gap-[6px] p-0 shrink-0"
        >
          <span>←</span>
          {isReviewMode ? 'Esci' : 'Verifiche'}
        </button>
      </div>

      {/* ── Scrollable body ───────────────────────────────────────── */}
      <div className="flex-1 overflow-y-auto">
        {/* design handoff: maxWidth 820px, px 48px, pt 44px, pb 80px */}
        <div className="max-w-[820px] mx-auto px-12 pt-11 pb-[80px]">
          {/* Hero: score + narrative */}
          {/* design handoff: gap 28px */}
          <div className="flex items-end gap-[28px] mb-[14px]">
            <div>
              {/* design handoff: fontSize 10px eyebrow */}
              <div className="text-[10px] text-ink-3 tracking-[0.12em] uppercase font-semibold mb-2">
                Punteggio
              </div>
              {/* design handoff: fontSize 64px for score headline */}
              <div
                className={clsx(
                  'font-mono text-[64px] font-medium tracking-[-0.02em] leading-none',
                  headlineColorClass,
                )}
              >
                {correct} {/* design handoff: fontSize 48px for denominator */}
                <span className="text-ink-4 text-[48px] font-normal">/ {total}</span>
              </div>
            </div>
            <div className="flex-1 pb-3">
              {/* design handoff: EB Garamond serif, fontSize 22px — font-serif maps to Source Serif 4 */}
              <p className="font-serif text-[22px] font-medium leading-[1.35] text-ink tracking-[-0.015em] m-0">
                {narrativeText}
              </p>
            </div>
          </div>

          {/* Stat row + CTAs */}
          <div className="flex gap-3 mt-6 flex-wrap items-center">
            <StatPill label="Corrette" n={correct} color="var(--green)" />
            <StatPill label="Errate" n={wrong} color="var(--red)" />
            <StatPill label="Saltate" n={skipped} color="var(--ink-3)" />
            <div className="flex-1" />
            <div className="flex items-center gap-[10px]">
              {onGoMastery != null && (
                <button
                  onClick={onGoMastery}
                  className="px-4 py-[10px] text-sm font-medium bg-surface text-ink-2 border border-line rounded-[9px] cursor-pointer font-sans whitespace-nowrap focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent focus-visible:outline-offset-2"
                >
                  Vedi la tua padronanza
                </button>
              )}
              <button
                onClick={onRetry}
                className="px-4 py-[10px] text-sm font-medium bg-surface text-ink-2 border border-line rounded-[9px] cursor-pointer font-sans whitespace-nowrap"
              >
                ↻ Rifai
              </button>
              {!isReadOnly && onGoReview != null && (
                <button
                  onClick={onGoReview}
                  className="px-[18px] py-[10px] text-sm font-semibold bg-ink text-bg border border-ink rounded-[9px] cursor-pointer font-sans inline-flex items-center gap-2 whitespace-nowrap"
                >
                  Vai a &ldquo;Da rivedere&rdquo; →
                </button>
              )}
            </div>
          </div>

          {/* Recap: unified list, original session order, with wrong-only filter */}
          <div className="mt-10">
            <div className="flex items-center justify-between mb-3">
              <div className="text-[10px] text-ink-3 tracking-[0.12em] uppercase font-semibold">
                Riepilogo
              </div>
              <div className="flex items-center gap-1 p-1 bg-surface border border-line rounded-[9px]">
                {(['all', 'wrong'] as const).map((f) => (
                  <button
                    key={f}
                    onClick={() => setFilter(f)}
                    className={clsx(
                      'px-3 py-[5px] rounded-[6px] text-xs font-medium cursor-pointer font-sans transition-colors',
                      filter === f ? 'bg-ink text-bg' : 'bg-transparent text-ink-3',
                    )}
                  >
                    {f === 'all' ? 'Tutte' : 'Solo errate'}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex flex-col gap-[10px]">
              {visibleItems.map(({ q, status, givenIdx }) => {
                const hasChapter = onGoToChapter != null && q.chapterRef != null
                const goToProps = hasChapter
                  ? { onGoToChapter: () => onGoToChapter!(q.chapterRef!.subParaId) }
                  : {}
                if (status === 'wrong')
                  return <WrongCard key={q.id} question={q} givenIdx={givenIdx!} {...goToProps} />
                if (status === 'correct')
                  return <CorrectCard key={q.id} question={q} {...goToProps} />
                return (
                  <div
                    key={q.id}
                    className="px-4 py-3 bg-surface border border-dashed border-line-2 rounded-[10px] text-[13.5px] text-ink-2 flex items-baseline gap-3 font-sans"
                  >
                    <span className="flex-1">{q.stem}</span>
                    <span className="text-[11px] text-gold shrink-0">saltata</span>
                  </div>
                )
              })}
              {visibleItems.length === 0 && (
                <div className="px-4 py-3 text-[13.5px] text-ink-3 font-sans">
                  Nessuna risposta errata.
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
